/**
 * useTakeHistory — iter-246
 *
 * Per-channel ring buffer of the most recent N takes (default 5).
 * Lives entirely in React state (in-memory) because AudioBuffers are
 * heavy — pushing them through localStorage would blow the quota.
 * When the user leaves / refreshes the studio, the history clears.
 *
 * Signature:
 *
 *   const {
 *     takesByChannel,         // { [ch]: [{ id, buffer, durationSec, capturedAt, label }] }
 *     pushTake(ch, buffer, label),
 *     revertTake(ch, takeId, engine),  // swaps engine.channels[ch].buffer with the saved take
 *     clearChannel(ch),
 *     clearAll(),
 *     countFor(ch),
 *     hasHistory(ch),
 *   } = useTakeHistory({ maxPerChannel: 5 });
 *
 * Reverting:
 *   - The current channel buffer is also pushed to history first so
 *     the revert is itself undoable.
 *   - We mutate `engine.channels[ch].buffer` directly because that's
 *     the source of truth for playback; the parent must `setLoadedCount`
 *     or rev-bump to trigger a re-render after revert.
 */
import { useCallback, useRef, useState } from "react";

const DEFAULT_MAX = 5;

let _idCounter = 0;
const nextId = () => `t${Date.now().toString(36)}-${++_idCounter}`;

export default function useTakeHistory({ maxPerChannel = DEFAULT_MAX } = {}) {
  const [takesByChannel, setTakesByChannel] = useState({});
  // mirror ref so callers (the recording hook) can read the latest
  // snapshot synchronously without depending on React state.
  const takesRef = useRef(takesByChannel);
  takesRef.current = takesByChannel;

  const pushTake = useCallback((ch, buffer, label) => {
    if (typeof ch !== "number" || ch < 0) return;
    if (!buffer || typeof buffer.duration !== "number") return;
    setTakesByChannel((prev) => {
      const list = prev[ch] || [];
      const next = [
        {
          id: nextId(),
          buffer,
          durationSec: buffer.duration,
          capturedAt: Date.now(),
          label: label || `Take ${list.length + 1}`,
        },
        ...list,
      ].slice(0, maxPerChannel);
      return { ...prev, [ch]: next };
    });
  }, [maxPerChannel]);

  const revertTake = useCallback((ch, takeId, engine) => {
    const list = takesRef.current[ch] || [];
    const take = list.find((t) => t.id === takeId);
    if (!take || !engine?.channels?.[ch]) return null;

    // First snapshot the CURRENT buffer so the revert itself is undoable.
    const currentBuffer = engine.channels[ch].buffer;
    if (currentBuffer && currentBuffer !== take.buffer) {
      pushTake(ch, currentBuffer, "Pre-revert");
    }

    // Swap channel buffer to the chosen take's buffer.
    engine.channels[ch].buffer = take.buffer;
    return take;
  }, [pushTake]);

  const clearChannel = useCallback((ch) => {
    setTakesByChannel((prev) => {
      if (!(ch in prev)) return prev;
      const next = { ...prev };
      delete next[ch];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setTakesByChannel({});
  }, []);

  const countFor = useCallback((ch) => (takesByChannel[ch] || []).length, [takesByChannel]);
  const hasHistory = useCallback((ch) => (takesByChannel[ch] || []).length > 0, [takesByChannel]);

  return {
    takesByChannel,
    pushTake,
    revertTake,
    clearChannel,
    clearAll,
    countFor,
    hasHistory,
  };
}
